import React, { useReducer, useState, useMemo } from "react";
import PropTypes from "prop-types";
import ColorTable from "../../elements/Tables/ColorTable/ColorTable";
import ColorCreationForm from "../../elements/Forms/ColorCreationForm/ColorCreationForm";

import { useFetch } from "../../../Utils/useFetch";

import {
  FETCH_ALL_COLOR_API,
} from "../../../Constants/Apis"
import { Button, Flex, Input, message, Modal, Typography } from "antd";
import { reducer } from "../../../Utils/CommonUtils";
import { deleteColor } from "../../../Services/ColorService";

const { Search } = Input;
const { Text } = Typography;

const ColorPage = ({ history, userData }) => {
  const initialState = {
    searchKeyword: "",
    filteredInfo: {},
    sorter: {},
  };

  const [state, dispatch] = useReducer(reducer, initialState);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [colorId, setColorId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState('code');
  const [sortOrder, setSortOrder] = useState('ascend');

  const handleOpenModal = () => setIsModalVisible(true);
  const handleCloseModal = () => setIsModalVisible(false);

  const handleDelete = async (id) => {
    setColorId(id);
    setIsModalOpen(true);
  };

  const handleOk = async () => {
    try {
      await deleteColor(colorId);
      message.success("Color deleted successfully");
      callReFetch();
    } catch (error) {
      message.error(
        error?.response?.data?.message ||
          "Something went wrong, failed to delete color."
      );
    }
    setIsModalOpen(false);
  };

  const {
    fetchedData: { data },
    callReFetch,
    loading,
  } = useFetch(`${FETCH_ALL_COLOR_API}`, state);

  const handleSearch = (value) => {
    dispatch({ type: "searchKeyword", payload: value });
    setCurrentPage(1); // Reset to first page when searching
    callReFetch();
  };

  // Process data for frontend pagination and sorting
  const processedData = useMemo(() => {
    if (!data) return { paginatedData: [], total: 0 };

    let filteredData = data;

    // Apply search filter
    if (state.searchKeyword) {
      filteredData = data.filter(item =>
        item.name?.toLowerCase().includes(state.searchKeyword.toLowerCase()) ||
        item.code?.toLowerCase().includes(state.searchKeyword.toLowerCase())
      );
    }

    // Apply sorting
    const sortedData = [...filteredData].sort((a, b) => {
      let aValue = a[sortField] || '';
      let bValue = b[sortField] || '';
      
      // Cast to int for numerical sorting
      const aInt = parseInt(aValue, 10);
      const bInt = parseInt(bValue, 10);
      
      // Use numerical comparison if both values are valid numbers
      if (!isNaN(aInt) && !isNaN(bInt)) {
        if (sortOrder === 'ascend') {
          return aInt - bInt;
        } else {
          return bInt - aInt;
        }
      }
      
      // Fallback to string comparison
      if (sortOrder === 'ascend') {
        return aValue.localeCompare(bValue);
      } else {
        return bValue.localeCompare(aValue);
      }
    });

    // Apply pagination
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedData = sortedData.slice(startIndex, endIndex);

    return {
      paginatedData,
      total: filteredData.length
    };
  }, [data, state.searchKeyword, currentPage, pageSize, sortField, sortOrder]);

  const handleTableChange = (pagination, filters, sorter) => {
    if (sorter.field !== sortField || sorter.order !== sortOrder) {
      setSortField(sorter.field || 'code');
      setSortOrder(sorter.order || 'ascend');
      setCurrentPage(1); // Reset to first page when sorting changes
    }
  };

  const handlePaginationChange = (page, size) => {
    setCurrentPage(page);
    if (size !== pageSize) {
      setPageSize(size);
      setCurrentPage(1); // Reset to first page when page size changes
    }
  };

  return (
    <div
      style={{
        width: "100vw",
        padding: "20px",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <div
        style={{
          paddingBottom: "20px",
          display: "flex",
          justifyContent: "space-between",
          width: "50vw",
          maxWidth: "100vw",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <Button type="primary" onClick={handleOpenModal}>
            Create New Color
          </Button>
          <Text strong>
            Total Colors: {processedData.total}
          </Text>
        </div>
        <Search
          placeholder="Search..."
          allowClear
          onSearch={handleSearch}
          style={{ width: "300px" }}
        />
      </div>

      <ColorTable
        history={history}
        loading={loading}
        data={processedData.paginatedData}
        userData={userData}
        callReFetch={callReFetch}
        dispatch={dispatch}
        handleDelete={handleDelete}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: processedData.total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} items`,
          onChange: handlePaginationChange,
          onShowSizeChange: handlePaginationChange,
        }}
        onChange={handleTableChange}
        sortField={sortField}
        sortOrder={sortOrder}
      />

      <Modal
        title="Create Color"
        open={isModalVisible}
        footer={null}
        onCancel={handleCloseModal}
      >
        <ColorCreationForm
          history={history}
          userData={userData}
          callReFetch={callReFetch}
          onClose={handleCloseModal}
        />
      </Modal>

      <Modal
        title="Delete Color"
        open={isModalOpen}
        onOk={handleOk}
        onCancel={() => setIsModalOpen(false)}
      >
        <p>Are you sure you want to delete this color?</p>
      </Modal>
    </div>
  );
};

ColorPage.propTypes = {};

export default ColorPage;
